/**
 * OncoAgentRefer — clinician Capture → Clarify → Route → Hand Off wizard
 * Production path: blocking clarify gate + vertical required fields + network routing.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import {
  saveReferralCapture,
  generateReferralPacket,
  submitReferral,
  getClinicId,
  getClinicSession,
  fetchClinicDirectory,
  fetchProtocolCaptureSchema,
} from '../api/client';
import { trackOncoAgentCTA, trackOncoAgentPageView } from '../utils/analytics';
import { getErrorMessage } from '../utils/errorHelpers';
import { inferVerticalId } from '../verticals/catalog';
import { 
  User, 
  ClipboardPlus, 
  MapPin, 
  Phone, 
  Award, 
  Compass, 
  Building2, 
  ArrowRight, 
  ArrowLeft,
  AlertTriangle,
  FileCheck,
  CheckCircle2,
  Calendar,
  Lock,
  Stethoscope,
  Clock,
  ShieldCheck,
  FileText
} from 'lucide-react';

const SYMPTOM_CHIPS = [
  'Oral ulcer >2 weeks',
  'Neck mass',
  'Hematuria',
  'Elevated PSA',
  'Changing mole',
  'Breast lump',
  'Hemoptysis',
  'Rectal bleeding',
  'Persistent bone pain',
  'Unintentional weight loss',
];

const CANCER_CHIPS = [
  'Oral / Head & Neck',
  'Prostate',
  'Bladder',
  'Melanoma',
  'Breast Cancer',
  'Lung Cancer',
  'Colorectal Cancer',
  'Ovarian / Gyn',
  'Lymphoma',
  'Pancreatic',
  'Brain / CNS',
  'Thyroid',
  'Sarcoma',
  'Other / Suspected',
];

const CLINICIAN_SPECIALTIES = [
  'Family Medicine / PCP',
  'Internal Medicine',
  'Dentistry / Oral Medicine',
  'Urology',
  'Dermatology',
  'Gynecology',
  'Orthopedics',
  'Other',
];

const VERTICAL_PRESET: Record<
  string,
  { cancer?: string; symptoms?: string[]; requiredCancerHint?: string }
> = {
  oral: {
    cancer: 'Oral / Head & Neck',
    symptoms: ['Oral ulcer >2 weeks'],
    requiredCancerHint: 'oral',
  },
  uro: {
    cancer: 'Bladder',
    symptoms: ['Hematuria'],
    requiredCancerHint: 'bladder|hematuria',
  },
  prostate: {
    cancer: 'Prostate',
    symptoms: ['Elevated PSA'],
    requiredCancerHint: 'prostate|psa',
  },
  skin: {
    cancer: 'Melanoma',
    symptoms: ['Changing mole'],
    requiredCancerHint: 'melanoma|skin',
  },
  breast: {
    cancer: 'Breast Cancer',
    symptoms: ['Breast lump'],
    requiredCancerHint: 'breast',
  },
  lung: {
    cancer: 'Lung Cancer',
    symptoms: ['Hemoptysis'],
    requiredCancerHint: 'lung',
  },
  colorectal: {
    cancer: 'Colorectal Cancer',
    symptoms: ['Rectal bleeding'],
    requiredCancerHint: 'colorectal|colon|rectal',
  },
};

type ClarifyItem = { why: string; suggestion: string; taskKey?: string; priority?: string };

export function OncoAgentRefer(): React.ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const verticalHint = (params.get('vertical') || '').toLowerCase();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [clinician, setClinician] = useState({
    name: '',
    specialty: 'Family Medicine / PCP',
    clinic: '',
    phone: '',
    email: '',
    npi: '',
  });
  const [location, setLocation] = useState('');
  const [patientName, setPatientName] = useState('');
  const preset = VERTICAL_PRESET[verticalHint];
  const [cancerType, setCancerType] = useState(preset?.cancer || '');
  const [symptoms, setSymptoms] = useState<string[]>(preset?.symptoms || []);
  const [customSymptom, setCustomSymptom] = useState('');
  const [timeline, setTimeline] = useState('');
  const [notes, setNotes] = useState('');
  const [patientSummary, setPatientSummary] = useState('');
  const [routePreview, setRoutePreview] = useState<{
    specialty?: string;
    urgency?: string;
    vertical?: string;
    rationale?: string[];
    redFlags?: Array<{ flag: string }>;
    clarify?: ClarifyItem[];
    protocolName?: string;
    pathways?: Array<{ title: string }>;
    clarifyGate?: {
      can_submit: boolean;
      blocking: ClarifyItem[];
      warnings: ClarifyItem[];
    };
  } | null>(null);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<
    Array<{ clinic_id: string; name: string; city?: string; state?: string }>
  >([]);
  const [destinationClinicId, setDestinationClinicId] = useState('');
  const [ackIncomplete, setAckIncomplete] = useState(false);
  const [ackReason, setAckReason] = useState('');
  const [phiConsent, setPhiConsent] = useState(false);
  const [confirmedUrgency, setConfirmedUrgency] = useState('');
  const [confirmedDestination, setConfirmedDestination] = useState('');
  const [captureSchema, setCaptureSchema] = useState<
    Array<{
      key: string;
      label: string;
      required?: boolean;
      field_type?: string;
      placeholder?: string;
      options?: string[];
    }>
  >([]);
  const [structuredFields, setStructuredFields] = useState<Record<string, string>>({});

  React.useEffect(() => {
    trackOncoAgentPageView('/oncoagent/refer', 'OncoAgent Clinician Referral');
    document.title = 'Start referral · OncoAgent';
    if (getClinicSession()) {
      fetchClinicDirectory()
        .then((d) => setDestinations(d.items || []))
        .catch(() => setDestinations([]));
    }
  }, []);

  React.useEffect(() => {
    const vertical = inferVerticalId(cancerType, verticalHint);
    fetchProtocolCaptureSchema(vertical, clinician.specialty)
      .then((r) => setCaptureSchema(r.fields || []))
      .catch(() => setCaptureSchema([]));
  }, [verticalHint, cancerType, clinician.specialty]);

  const canStep1 =
    clinician.name.trim().length > 1 &&
    clinician.specialty &&
    clinician.phone.trim().length >= 7 &&
    patientName.trim().length > 1;
  const canStep2 =
    symptoms.length > 0 &&
    timeline.trim().length > 3 &&
    Boolean(cancerType) &&
    (!preset?.requiredCancerHint ||
      new RegExp(preset.requiredCancerHint, 'i').test(cancerType));

  const toggleSymptom = (s: string) => {
    setSymptoms((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const allSymptoms = useMemo(() => {
    const extra = customSymptom.trim();
    return extra && !symptoms.includes(extra) ? [...symptoms, extra] : symptoms;
  }, [symptoms, customSymptom]);

  const blocking = routePreview?.clarifyGate?.blocking || [];
  const warnings = routePreview?.clarifyGate?.warnings || [];
  const needsAck = blocking.length > 0;

  const runCapture = async () => {
    setBusy(true);
    setError(null);
    setAckIncomplete(false);
    setAckReason('');
    try {
      let id = caseId;
      if (!id) {
        // M0: attach X-Clinic-Session here so client.ts stays unchanged.
        const session = getClinicSession();
        const created = await axios.post(
          `${BACKEND_URL}/api/cases`,
          {
            patient_name: patientName.trim() || 'Referral patient',
            cancer_type: cancerType || undefined,
            country: 'United States',
            location: location || undefined,
            product_surface: 'clinician',
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              ...(session ? { 'X-Clinic-Session': session } : {}),
            },
          }
        );
        id = created.data.caseId;
        setCaseId(id);
      }

      const clinicId = getClinicId() || undefined;
      const result = await saveReferralCapture(id, {
        referring_clinician: {
          name: clinician.name.trim(),
          specialty: clinician.specialty,
          clinic: clinician.clinic || undefined,
          phone: clinician.phone || undefined,
          email: clinician.email || undefined,
          npi: clinician.npi || undefined,
          clinic_id: clinicId,
        },
        symptoms: allSymptoms,
        timeline: timeline.trim(),
        notes: notes.trim() || undefined,
        suspected_cancer_type: cancerType || undefined,
        patient_summary: patientSummary.trim() || undefined,
        destination_clinic_id: destinationClinicId || undefined,
        structured_fields: Object.keys(structuredFields).length
          ? Object.fromEntries(
              Object.entries(structuredFields).filter(([, v]) => String(v || '').trim())
            )
          : undefined,
      });

      setRoutePreview({
        specialty: result.route?.specialty,
        urgency: result.route?.urgency,
        vertical: result.route?.vertical,
        rationale: result.route?.rationale,
        redFlags: (result.redFlags || (result as any).red_flags || []).map((f: any) => ({
          flag: f.flag,
        })),
        clarify: (result.clarifyPrompts || (result as any).clarify_prompts || []).map(
          (c: any) => ({
            why: c.why,
            suggestion: c.suggestion,
            taskKey: c.taskKey,
            priority: c.priority,
          })
        ),
        protocolName: (result as any).protocol?.name,
        pathways: ((result as any).matched_pathways || []).map((p: any) => ({
          title: p.title,
        })),
        clarifyGate: (result as any).clarifyGate || (result as any).clarify_gate,
      });
      setConfirmedUrgency(result.route?.urgency || 'normal');
      setConfirmedDestination(result.route?.specialty || '');
      trackOncoAgentCTA('referral_capture', 'refer_wizard');
      setStep(3);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to save referral');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateDecision = async (urg: string, dest: string) => {
    if (!caseId) return;
    setBusy(true);
    setError(null);
    try {
      const clinicId = getClinicId() || undefined;
      const result = await saveReferralCapture(caseId, {
        referring_clinician: {
          name: clinician.name.trim(),
          specialty: clinician.specialty,
          clinic: clinician.clinic || undefined,
          phone: clinician.phone || undefined,
          email: clinician.email || undefined,
          npi: clinician.npi || undefined,
          clinic_id: clinicId,
        },
        symptoms: allSymptoms,
        timeline: timeline.trim(),
        notes: notes.trim() || undefined,
        suspected_cancer_type: cancerType || undefined,
        patient_summary: patientSummary.trim() || undefined,
        destination_clinic_id: destinationClinicId || undefined,
        structured_fields: Object.keys(structuredFields).length
          ? Object.fromEntries(
              Object.entries(structuredFields).filter(([, v]) => String(v || '').trim())
            )
          : undefined,
        clinician_decisions: {
          urgency: urg,
          destination: dest,
        },
      } as any);

      setConfirmedUrgency(urg);
      setConfirmedDestination(dest);
      setRoutePreview({
        specialty: result.route?.specialty,
        urgency: result.route?.urgency,
        vertical: result.route?.vertical,
        rationale: result.route?.rationale,
        redFlags: (result.redFlags || (result as any).red_flags || []).map((f: any) => ({
          flag: f.flag,
        })),
        clarify: (result.clarifyPrompts || (result as any).clarify_prompts || []).map(
          (c: any) => ({
            why: c.why,
            suggestion: c.suggestion,
            taskKey: c.taskKey,
            priority: c.priority,
          })
        ),
        protocolName: (result as any).protocol?.name,
        pathways: ((result as any).matched_pathways || []).map((p: any) => ({
          title: p.title,
        })),
        clarifyGate: (result as any).clarifyGate || (result as any).clarify_gate,
      });
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to update clinical decision');
    } finally {
      setBusy(false);
    }
  };

  const finishSubmit = async () => {
    if (!caseId) return;
    if (!phiConsent) {
      setError(
        'Confirm you are authorized to share this clinical packet with the destination oncology clinic.'
      );
      return;
    }
    if (needsAck && (!ackIncomplete || ackReason.trim().length < 8)) {
      setError(
        'Blocking clarify items remain. Edit the capture, or acknowledge incomplete handoff with a short reason.'
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await generateReferralPacket(caseId);
      const submitted = await submitReferral(caseId, {
        phi_handoff_consent: true,
        ...(destinationClinicId ? { destination_clinic_id: destinationClinicId } : {}),
        ...(needsAck
          ? {
              acknowledge_incomplete: true,
              acknowledge_reason: ackReason.trim(),
            }
          : {}),
      });
      trackOncoAgentCTA('referral_submit', 'refer_wizard');
      const share = (submitted as any)?.tracking?.share_token;
      const destName = (submitted as any)?.destination_clinic_name;
      const qs = new URLSearchParams();
      if (share) qs.set('tracking', share);
      qs.set('submitted', '1');
      if (destName) qs.set('dest', destName);
      navigate(`/oncoagent/case/${caseId}?${qs.toString()}`);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'CLARIFY_REQUIRED' || detail?.clarifyGate) {
        const gate = detail.clarifyGate || detail.extra?.clarifyGate;
        setRoutePreview((prev) =>
          prev
            ? {
                ...prev,
                clarifyGate: gate || prev.clarifyGate,
              }
            : prev
        );
        setError(
          detail?.message ||
            'Complete blocking clarify items or acknowledge incomplete handoff before submit.'
        );
      } else {
        setError(getErrorMessage(err) || 'Failed to submit referral');
      }
      setBusy(false);
    }
  };

  return (
    <OaWorkspaceShell
      kicker="Capture → Clarify → Route → Hand off"
      title="Structure a cancer referral"
      subtitle="Capture patient presentations, satisfy target oncology protocols, and dispatch unified immutable handoff packets."
    >
      {/* SaaS-Grade Professional Stepper */}
      <div className="mb-8" aria-hidden="true">
        <div className="flex items-center justify-between max-w-xl mx-auto">
          {[
            { num: 1, name: 'Clinician Context' },
            { num: 2, name: 'Capture Findings' },
            { num: 3, name: 'Routing & Handoff' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  step >= s.num 
                    ? 'bg-[#0f3e17] text-white ring-4 ring-[#0f3e17]/10' 
                    : 'bg-white text-[#6d6a63] border border-[#e6e2d8]'
                }`}>
                  {s.num}
                </div>
                <span className={`text-xs font-semibold tracking-tight transition-colors ${
                  step >= s.num ? 'text-[#161616]' : 'text-[#6d6a63]'
                }`}>
                  {s.name}
                </span>
              </div>
              {idx < 2 && (
                <div className={`flex-1 h-0.5 mx-4 transition-all duration-500 ${
                  step > s.num ? 'bg-[#0f3e17]' : 'bg-[#e6e2d8]'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {error ? <div className="oa-ws-error mb-6">{error}</div> : null}

      {/* Step 1: Clinician & Patient Context */}
      {step === 1 && (
        <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-6">
          <div className="border-b border-[#f6f4ef] pb-4 flex items-center gap-3">
            <div className="bg-emerald-50 p-2.5 rounded-xl text-[#0f3e17]">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#161616] m-0">Referring Clinician & Patient</h2>
              <p className="text-xs text-[#6d6a63] m-0 mt-0.5">Define who is referring, where the patient is located, and routing targets.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Your name</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50 focus:ring-1 focus:ring-[#0f3e17]/20"
                value={clinician.name}
                onChange={(e) => setClinician({ ...clinician, name: e.target.value })}
                placeholder="Dr. Jane Smith"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Your specialty</label>
              <select
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={clinician.specialty}
                onChange={(e) => setClinician({ ...clinician, specialty: e.target.value })}
              >
                {CLINICIAN_SPECIALTIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Clinic / practice</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={clinician.clinic}
                onChange={(e) => setClinician({ ...clinician, clinic: e.target.value })}
                placeholder="Austin Family Health"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Patient name (required)</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder="Patient full name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Patient location</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Austin, TX"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">Phone (required)</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={clinician.phone}
                onChange={(e) => setClinician({ ...clinician, phone: e.target.value })}
                placeholder="+1 555 0100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#161616] mb-1.5">NPI (optional)</label>
              <input
                className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                value={clinician.npi}
                onChange={(e) => setClinician({ ...clinician, npi: e.target.value })}
                placeholder="10-digit NPI"
              />
            </div>
            {destinations.length > 0 ? (
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-[#161616] mb-1.5">Destination clinic (network)</label>
                <select
                  className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                  value={destinationClinicId}
                  onChange={(e) => setDestinationClinicId(e.target.value)}
                >
                  <option value="">Same clinic / unassigned queue</option>
                  {destinations.map((d) => (
                    <option key={d.clinic_id} value={d.clinic_id}>
                      {d.name}
                      {d.city ? ` · ${d.city}` : ''}
                      {d.state ? `, ${d.state}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-[#6d6a63] mt-1.5 leading-relaxed flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#0f3e17]" />
                  <span>Routes the referral securely into the receiving clinic&apos;s active workspace.</span>
                </p>
              </div>
            ) : getClinicSession() ? null : (
              <div className="md:col-span-2 bg-[#f6f4ef]/50 border border-[#e6e2d8] rounded-xl p-4 mt-1">
                <p className="text-xs text-[#6d6a63] leading-relaxed m-0">
                  <Link to="/oncoagent/app/clinic" className="text-[#0f3e17] font-semibold underline hover:text-[#0c3312]">
                    Sign in to your clinic workspace
                  </Link>{' '}
                  to securely route this patient across certified receiving networks.
                </p>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-[#f6f4ef] flex justify-end">
            <button
              type="button"
              className="bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-all duration-300 shadow-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canStep1}
              onClick={() => setStep(2)}
            >
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Clinical Findings & Evidence */}
      {step === 2 && (
        <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-6">
          <div className="border-b border-[#f6f4ef] pb-4 flex items-center gap-3">
            <div className="bg-emerald-50 p-2.5 rounded-xl text-[#0f3e17]">
              <ClipboardPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#161616] m-0">Capture Clinical Findings</h2>
              <p className="text-xs text-[#6d6a63] m-0 mt-0.5">Specify cancer vertical, symptoms, timelines, and specialized capture fields.</p>
            </div>
          </div>

          {verticalHint ? (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-3.5 text-xs text-[#0f3e17]">
              Preset clinical filter active: <strong>{verticalHint}</strong>. We have loaded guidelines specific to this suspicion.
            </div>
          ) : null}

          {/* Cancer Vertical Selection */}
          <div>
            <label className="block text-xs font-bold text-[#161616] mb-2 uppercase tracking-wider">Suspected cancer type (required)</label>
            <div className="flex flex-wrap gap-2">
              {CANCER_CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`text-xs px-3.5 py-1.5 rounded-lg border font-medium transition-all ${
                    cancerType === c 
                      ? 'bg-[#0f3e17] text-white border-[#0f3e17] shadow-sm font-semibold' 
                      : 'bg-[#f6f4ef] text-[#6d6a63] border-[#e6e2d8] hover:text-[#161616]'
                  }`}
                  onClick={() => setCancerType(cancerType === c ? '' : c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Clinical Findings & Symptoms Checklist */}
          <div>
            <label className="block text-xs font-bold text-[#161616] mb-2 uppercase tracking-wider">Symptoms / clinical presentation</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {SYMPTOM_CHIPS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    symptoms.includes(s) 
                      ? 'bg-[#0f3e17]/10 text-[#0f3e17] border-[#0f3e17]/30 font-semibold' 
                      : 'bg-white text-[#6d6a63] border-[#e6e2d8] hover:text-[#161616]'
                  }`}
                  onClick={() => toggleSymptom(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <input
              className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-2 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
              placeholder="Type to append another finding..."
              value={customSymptom}
              onChange={(e) => setCustomSymptom(e.target.value)}
            />
          </div>

          {/* Dynamic Protocol Fields */}
          {captureSchema.length > 0 ? (
            <div className="bg-[#f6f4ef]/50 border border-[#e6e2d8] rounded-2xl p-5 space-y-4">
              <div>
                <h3 className="text-xs font-bold text-[#161616] m-0 uppercase tracking-wider">Protocol clinical data capture</h3>
                <p className="text-xs text-[#6d6a63] mt-1 leading-relaxed">
                  These high-fidelity fields correspond directly with matching guideline criteria. Completing them reduces specialist triage rejection.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {captureSchema.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-[#161616] mb-1">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </label>
                    {f.field_type === 'select' && f.options?.length ? (
                      <select
                        className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-1.5 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                        value={structuredFields[f.key] || ''}
                        onChange={(e) =>
                          setStructuredFields({ ...structuredFields, [f.key]: e.target.value })
                        }
                      >
                        <option value="">Select…</option>
                        {f.options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="bg-white border border-[#e6e2d8] rounded-lg px-3 py-1.5 text-sm text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                        type={f.field_type === 'number' ? 'number' : 'text'}
                        placeholder={f.placeholder || ''}
                        value={structuredFields[f.key] || ''}
                        onChange={(e) =>
                          setStructuredFields({ ...structuredFields, [f.key]: e.target.value })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Timeline & Notes Text Areas */}
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-xs font-bold text-[#161616] mb-1.5 uppercase tracking-wider">Timeline (include onset, duration, progression)</label>
              <textarea
                className="bg-white border border-[#e6e2d8] rounded-xl px-3.5 py-2.5 text-sm text-[#161616] w-full h-24 focus:outline-none focus:border-[#0f3e17]/50 focus:ring-1 focus:ring-[#0f3e17]/20"
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                placeholder="e.g. Oral ulcer on left lateral tongue for 6 weeks; tobacco 20 pack-years; progressive enlargement."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#161616] mb-1.5 uppercase tracking-wider">Clinical notes (labs, imaging, biopsy)</label>
              <textarea
                className="bg-white border border-[#e6e2d8] rounded-xl px-3.5 py-2.5 text-sm text-[#161616] w-full h-20 focus:outline-none focus:border-[#0f3e17]/50 focus:ring-1 focus:ring-[#0f3e17]/20"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Prior workup: CT/MRI, pathology, relevant blood counts..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#161616] mb-1.5 uppercase tracking-wider">Patient summary for oncologist</label>
              <textarea
                className="bg-white border border-[#e6e2d8] rounded-xl px-3.5 py-2.5 text-sm text-[#161616] w-full h-20 focus:outline-none focus:border-[#0f3e17]/50 focus:ring-1 focus:ring-[#0f3e17]/20"
                value={patientSummary}
                onChange={(e) => setPatientSummary(e.target.value)}
                placeholder="A brief executive overview summarizing why standard fast-track criteria are met."
              />
            </div>
          </div>

          <div className="pt-4 border-t border-[#f6f4ef] flex items-center justify-between">
            <button 
              type="button" 
              className="inline-flex items-center gap-1 bg-[#f6f4ef] hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors border border-[#e6e2d8]" 
              onClick={() => setStep(1)}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <button
              type="button"
              className="bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-all duration-300 shadow-sm flex items-center gap-1.5 disabled:opacity-50"
              disabled={!canStep2 || busy}
              onClick={runCapture}
            >
              <span>{busy ? 'Evaluating...' : 'Detect red flags & route'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Route Verification, Clarify, & Submit */}
      {step === 3 && routePreview && (
        <div className="space-y-6">
          {/* M1 Clinical Spine Validation */}
          {routePreview.vertical === 'oral' && (
            <div className="bg-emerald-50/50 border-2 border-[#0f3e17]/20 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-[#0f3e17]/10 pb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-[#0f3e17] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    Clinician Confirmation
                  </span>
                  <span className="text-xs text-[#6d6a63]">Milestone M1 Validation Spine</span>
                </div>
                <p className="text-[10px] text-[#0f3e17] font-semibold bg-emerald-100/50 px-2 py-0.5 rounded m-0">
                  ✓ Oral Lesion Protocol Active
                </p>
              </div>
              <p className="text-xs text-[#6d6a63] leading-relaxed m-0">
                Verify or adjust the clinical pathway routing recommendation below. Unconfirmed pathways default safely to standard priority.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <p className="text-xs font-bold text-[#161616] mb-2 uppercase tracking-wider">Pathway Urgency</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={`text-xs px-3.5 py-1.5 rounded-lg border transition-all ${
                        confirmedUrgency === 'urgent' 
                          ? 'bg-[#0f3e17] text-white font-semibold' 
                          : 'bg-white text-[#6d6a63] border-[#e6e2d8]'
                      }`}
                      onClick={() => handleUpdateDecision('urgent', confirmedDestination)}
                    >
                      Urgent (Recommended)
                    </button>
                    <button
                      type="button"
                      className={`text-xs px-3.5 py-1.5 rounded-lg border transition-all ${
                        confirmedUrgency === 'normal' 
                          ? 'bg-[#0f3e17] text-white font-semibold' 
                          : 'bg-white text-[#6d6a63] border-[#e6e2d8]'
                      }`}
                      onClick={() => handleUpdateDecision('normal', confirmedDestination)}
                    >
                      Normal (Routine)
                    </button>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-bold text-[#161616] mb-2 uppercase tracking-wider">Target Specialty</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={`text-xs px-3.5 py-1.5 rounded-lg border transition-all ${
                        confirmedDestination === 'Head & Neck / Oral Oncology' 
                          ? 'bg-[#0f3e17] text-white font-semibold' 
                          : 'bg-white text-[#6d6a63] border-[#e6e2d8]'
                      }`}
                      onClick={() => handleUpdateDecision(confirmedUrgency, 'Head & Neck / Oral Oncology')}
                    >
                      Head & Neck / Oral Oncology
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="pt-2">
                <p className="text-xs text-[#2f6f3e] font-medium flex items-center gap-1 leading-relaxed m-0">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    {confirmedUrgency === 'urgent' 
                      ? 'Decision recorded: Urgent pathway active (30-min handoff SLA enabled).' 
                      : 'Decision recorded: Routine pathway selected.'}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Recommended Route Card */}
          <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] font-bold text-[#6d6a63] uppercase tracking-wider">AI routing recommendation</span>
                <h2 className="text-2xl font-bold text-[#161616] mt-0.5 tracking-tight">{routePreview.specialty}</h2>
              </div>
              <span className={`text-[10px] font-bold px-3 py-1 rounded-full border uppercase tracking-wider ${
                routePreview.urgency === 'urgent' || routePreview.urgency === 'emergent'
                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                  : 'bg-emerald-50 text-emerald-800 border-emerald-100'
              }`}>
                {(routePreview.urgency || 'normal').toUpperCase()}
                {routePreview.vertical ? ` &middot; ${routePreview.vertical}` : ''}
              </span>
            </div>

            {routePreview.protocolName && (
              <p className="text-xs text-[#161616] font-medium bg-[#f6f4ef] px-3 py-2 rounded-lg border border-[#e6e2d8] inline-block m-0">
                Matched Protocol: <strong className="text-[#0f3e17]">{routePreview.protocolName}</strong>
              </p>
            )}

            {routePreview.rationale?.length ? (
              <div className="space-y-2 pt-2">
                <p className="text-xs font-bold text-[#161616] uppercase tracking-wider m-0">Guideline Rationale</p>
                <ul className="space-y-1.5 text-xs text-[#6d6a63] list-inside list-disc leading-relaxed">
                  {routePreview.rationale.map((r) => (
                    <li key={r} className="marker:text-[#0f3e17]">{r}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {destinationClinicId ? (
              <div className="bg-[#f6f4ef]/50 border border-[#e6e2d8] rounded-xl p-3.5 text-xs">
                <span>Direct dispatch target: </span>
                <strong className="text-[#161616]">
                  {destinations.find((d) => d.clinic_id === destinationClinicId)?.name || 'Selected Network Clinic'}
                </strong>
                <span>. Secure clinician-to-clinician handoff occurs upon packet generation.</span>
              </div>
            ) : (
              <p className="text-xs text-[#6d6a63] leading-relaxed italic m-0">
                No destination clinic assigned. This referral stays in your local clinician queue (unassigned clinic network).
              </p>
            )}
          </div>

          {/* Red Flags Summary */}
          {routePreview.redFlags && routePreview.redFlags.length > 0 ? (
            <div className="bg-rose-50/80 border border-rose-200 rounded-2xl p-6 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-rose-800">
                <AlertTriangle className="w-5 h-5 text-rose-600 animate-pulse" />
                <h3 className="font-semibold text-base m-0">Triggered Cancer Red Flags</h3>
              </div>
              <ul className="flex flex-wrap gap-2">
                {routePreview.redFlags.map((f) => (
                  <li key={f.flag} className="bg-white text-rose-800 border border-rose-100 text-xs font-semibold px-3 py-1 rounded-lg">
                    {f.flag}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Matched Pathways */}
          {routePreview.pathways && routePreview.pathways.length > 0 ? (
            <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-[#161616] uppercase tracking-wider m-0">Active pathways matched</h3>
              <ul className="space-y-1.5 text-xs text-[#6d6a63] list-disc list-inside">
                {routePreview.pathways.map((p) => (
                  <li key={p.title} className="marker:text-[#0f3e17]">{p.title}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Blocking Clarification Panel */}
          {blocking.length > 0 ? (
            <div className="bg-rose-50/50 border-2 border-rose-200 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2 text-rose-800">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                <h3 className="font-semibold text-base m-0">Incomplete Referral Checklist</h3>
              </div>
              <p className="text-xs text-[#6d6a63] leading-relaxed m-0">
                The following required clinical items were not answered. Address them in capture, or certify clinical reason to submit an incomplete packet.
              </p>
              
              <ul className="space-y-2.5">
                {blocking.map((c) => (
                  <li key={c.taskKey || c.suggestion} className="bg-white border border-rose-100 rounded-xl p-3.5 shadow-xs">
                    <div className="flex items-center gap-2">
                      <span className="bg-rose-100 text-rose-800 text-[9px] font-bold px-2 py-0.5 rounded uppercase">Required</span>
                      <strong className="text-xs text-[#161616]">{c.why}</strong>
                    </div>
                    <div className="text-xs text-[#6d6a63] mt-1 leading-relaxed pl-14">{c.suggestion}</div>
                  </li>
                ))}
              </ul>

              <div className="border-t border-rose-200/50 pt-3 space-y-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded text-[#0f3e17] focus:ring-[#0f3e17]"
                    checked={ackIncomplete}
                    onChange={(e) => setAckIncomplete(e.target.checked)}
                  />
                  <span className="text-xs text-rose-900 font-medium">
                    I acknowledge this clinical handoff is incomplete and certify there is an overriding safety or access reason to submit immediately.
                  </span>
                </label>
                {ackIncomplete && (
                  <textarea
                    className="bg-white border border-rose-200 rounded-xl px-3 py-2 text-xs text-[#161616] w-full h-16 focus:outline-none focus:border-rose-400"
                    value={ackReason}
                    onChange={(e) => setAckReason(e.target.value)}
                    placeholder="Certify clinical override reason (minimum 8 characters)"
                  />
                )}
              </div>
            </div>
          ) : null}

          {/* Recommended Warnings Checklist */}
          {warnings.length > 0 ? (
            <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-[#161616] uppercase tracking-wider m-0">Suggested additions</h3>
              <ul className="space-y-3">
                {warnings.map((c) => (
                  <li key={c.taskKey || c.suggestion} className="border-b border-dashed border-[#f6f4ef] pb-2.5 last:border-0 last:pb-0">
                    <strong className="text-xs text-[#161616]">{c.why}</strong>
                    <div className="text-xs text-[#6d6a63] mt-0.5">{c.suggestion}</div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* PHI Handoff Consent Checkbox */}
          <div className="bg-white border border-[#e6e2d8] rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-[#0f3e17] border-b border-[#f6f4ef] pb-3">
              <Lock className="w-4 h-4" />
              <h3 className="text-xs font-bold uppercase tracking-wider m-0">PHI Handoff Consent</h3>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 rounded text-[#0f3e17] focus:ring-[#0f3e17]"
                checked={phiConsent}
                onChange={(e) => setPhiConsent(e.target.checked)}
              />
              <span className="text-xs text-[#6d6a63] leading-relaxed">
                I authorize secure coordination and share this patient&apos;s PHI packet with target oncology clinics in compliance with network security standards. I understand OncoAgent does not replace certified clinician review.
              </span>
            </label>
          </div>

          {/* Step 3 Action Buttons */}
          <div className="pt-4 border-t border-[#f6f4ef] flex items-center justify-between">
            <button 
              type="button" 
              className="inline-flex items-center gap-1 bg-[#f6f4ef] hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors border border-[#e6e2d8]" 
              disabled={busy}
              onClick={() => setStep(2)}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Edit Capture</span>
            </button>
            <button
              type="button"
              className="bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-all duration-300 shadow-sm flex items-center gap-1.5 disabled:opacity-50"
              disabled={
                busy ||
                !phiConsent ||
                (needsAck && (!ackIncomplete || ackReason.trim().length < 8))
              }
              onClick={finishSubmit}
            >
              {busy
                ? 'Routing & Submitting…'
                : !phiConsent
                  ? 'Confirm Consent to Submit'
                  : needsAck && !ackIncomplete
                    ? 'Acknowledge Overrides to Submit'
                    : 'Acknowledge & Submit to Clinic'}
            </button>
          </div>
        </div>
      )}
    </OaWorkspaceShell>
  );
}

export default OncoAgentRefer;
